import { ArrowLeft } from 'lucide-react';
import { Button, Inline, Stack, Text } from '@relay-towing/ui';
import { cn } from '@/lib/utils';

/**
 * The shapes each inspection screen draws while its first read is in flight.
 *
 * A driver on a yard phone gets a worse deal from a spinner than anyone: half
 * the time it is the whole screen, so there is nothing to say whether they
 * opened the right truck until the request lands. These draw the screen that
 * is coming, at the size it will be, so nothing jumps.
 *
 * Deliberately still: no pulse, no shimmer. A block that throbs pulls the eye
 * to the one part of the screen with nothing in it. The blocks are `bg-muted`
 * and they sit there.
 *
 * The back button is real and wired in every one of these. Leaving is the one
 * thing a driver might want to do while a screen is still loading.
 */

/** One block of nothing, shaped like the text that will replace it. */
function SkeletonBar({ className }: { className?: string }) {
  return <span className={cn('block rounded bg-muted', className)} />;
}

/** Rows are not all the same length, and a stack of equal bars reads as a table. */
const LINES = ['w-36', 'w-28', 'w-40', 'w-32', 'w-24', 'w-36'];

/**
 * A card of tappable rows: the truck list, and the history list.
 *
 * Both are an icon tile, two lines and a chevron, so one shape covers the
 * pair. The sizes track the real rows in InspectionsScreen, so the list does
 * not jump when it lands.
 */
export function RowListSkeleton({
  rows = 5,
  label,
}: {
  rows?: number;
  label: string;
}) {
  return (
    <div
      role="status"
      aria-label={label}
      className="divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card"
    >
      {Array.from({ length: rows }, (_, row) => (
        <div key={row} className="flex items-center gap-3 px-4 py-3">
          <SkeletonBar className="size-9 shrink-0 rounded-lg" />
          <Stack gap={1} className="min-w-0 flex-1">
            <SkeletonBar className="h-4 w-24" />
            <SkeletonBar className={cn('h-3', LINES[row % LINES.length])} />
          </Stack>
          <SkeletonBar className="size-icon-sm shrink-0 rounded-sm" />
        </div>
      ))}
    </div>
  );
}

/** A section of a finished report, drawn empty: a question and its answer. */
function QuestionsSkeleton({ rows }: { rows: number }) {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      {Array.from({ length: rows }, (_, row) => (
        <div
          key={row}
          className={cn(
            'flex items-start justify-between gap-3 p-4',
            row > 0 && 'border-t border-border'
          )}
        >
          <Stack gap={1} className="min-w-0 flex-1">
            <SkeletonBar className={cn('h-4', LINES[row % LINES.length])} />
            <SkeletonBar className="h-3 w-20" />
          </Stack>
          <SkeletonBar className="h-4 w-12 shrink-0" />
        </div>
      ))}
    </div>
  );
}

/**
 * A question on the blank checklist: one card, a label, and the pass/fail pair
 * underneath it. Its own shape rather than the report's, because the run
 * screen gives every question a card of its own.
 */
function RunQuestionsSkeleton({ rows }: { rows: number }) {
  return (
    <Stack gap={3}>
      {Array.from({ length: rows }, (_, row) => (
        <Stack
          key={row}
          gap={4}
          className="rounded-xl border border-border bg-card p-4"
        >
          <Stack gap={1}>
            <SkeletonBar className={cn('h-4', LINES[row % LINES.length])} />
            <SkeletonBar className="h-3 w-20" />
          </Stack>
          <Inline gap={2}>
            <SkeletonBar className="h-11 flex-1 rounded-md" />
            <SkeletonBar className="h-11 flex-1 rounded-md" />
          </Inline>
        </Stack>
      ))}
    </Stack>
  );
}

/** The blank checklist, while the server is handing one out. */
export function RunScreenSkeleton({ onBack }: { onBack: () => void }) {
  return (
    <div
      role="status"
      aria-label="Loading the checklist"
      className="min-h-screen bg-background pb-28"
    >
      <header className="sticky top-0 z-10 border-b border-border bg-background/90 backdrop-blur">
        <Inline gap={3} align="center" className="px-4 py-3">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Back to inspections"
            onClick={onBack}
          >
            <ArrowLeft />
          </Button>
          <Text as="h1" variant="body-lg-strong">
            Inspection
          </Text>
        </Inline>
      </header>

      <Stack gap={4} className="px-4 py-4">
        {/* The summary card: truck, template, and the meta line under the rule. */}
        <Stack gap={3} className="rounded-2xl border border-border bg-card p-4">
          <Inline gap={3} align="start" justify="between">
            <Stack gap={1} className="min-w-0">
              <SkeletonBar className="h-6 w-40" />
              <SkeletonBar className="h-3 w-28" />
            </Stack>
            <SkeletonBar className="h-5 w-24 shrink-0 rounded-pill" />
          </Inline>
          <div className="border-t border-border pt-3">
            <SkeletonBar className="h-3 w-48" />
          </div>
        </Stack>

        <Stack gap={2}>
          <Inline gap={3} justify="between">
            <SkeletonBar className="h-3 w-24" />
            <SkeletonBar className="h-3 w-8" />
          </Inline>
          {/* The progress rail, at nothing done, which is where it starts anyway. */}
          <div className="h-2 w-full rounded-pill bg-muted" />
        </Stack>

        {[0, 1].map((section) => (
          <Stack key={section} gap={3}>
            <SkeletonBar className="mx-1 h-3 w-24" />
            <RunQuestionsSkeleton rows={2} />
          </Stack>
        ))}
      </Stack>
    </div>
  );
}

/** A finished report, while it is being read back. */
export function ReportScreenSkeleton({ onBack }: { onBack: () => void }) {
  return (
    <div
      role="status"
      aria-label="Loading the report"
      className="min-h-screen bg-background pb-8"
    >
      <header className="sticky top-0 z-10 border-b border-border bg-card px-4 py-3">
        <Button variant="link" size="inline" onClick={onBack}>
          <ArrowLeft />
          Inspections
        </Button>
      </header>

      <Stack gap={4} className="p-4">
        {/* The summary card: truck, template and result, odometer under the
            rule. Same shape as the run screen's, because it is the same card. */}
        <Stack gap={3} className="rounded-2xl border border-border bg-card p-4">
          <Inline gap={3} align="start" justify="between">
            <Stack gap={1} className="min-w-0">
              <SkeletonBar className="h-6 w-40" />
              <SkeletonBar className="h-3 w-44" />
            </Stack>
            <SkeletonBar className="h-5 w-20 shrink-0 rounded-pill" />
          </Inline>
          <div className="border-t border-border pt-3">
            <SkeletonBar className="h-3 w-32" />
          </div>
        </Stack>

        {/* The pass/fail banner. Tone would be a guess, so it stays neutral. */}
        <SkeletonBar className="h-14 w-full rounded-xl" />

        {[0, 1].map((section) => (
          <Stack key={section} gap={3}>
            <SkeletonBar className="mx-1 h-4 w-24" />
            <QuestionsSkeleton rows={4} />
          </Stack>
        ))}

        {/* The signed-off card. Whether there are notes, photos or a place is
            anyone's guess, but a filed report is nearly always signed. */}
        <Stack gap={3} className="rounded-xl border border-border bg-card p-4">
          <SkeletonBar className="h-4 w-24" />
          <SkeletonBar className="h-16 w-full rounded-lg" />
          <SkeletonBar className="h-3 w-32" />
        </Stack>
      </Stack>
    </div>
  );
}

/** The last check, opened, while the report behind it loads. */
export function LastCheckSkeleton() {
  return (
    <Stack gap={4} role="status" aria-label="Loading the last report">
      <Inline gap={4} wrap>
        {[0, 1].map((pair) => (
          <Stack key={pair} gap={1}>
            <SkeletonBar className="h-3 w-16" />
            <SkeletonBar className="h-4 w-20" />
          </Stack>
        ))}
      </Inline>
      <Stack gap={1}>
        <SkeletonBar className="h-3 w-20" />
        {LINES.slice(0, 4).map((width) => (
          <Inline
            key={width}
            justify="between"
            align="start"
            gap={3}
            className="border-b border-border py-1.5 last:border-b-0"
          >
            <SkeletonBar className={cn('h-3', width)} />
            <SkeletonBar className="h-3 w-10 shrink-0" />
          </Inline>
        ))}
      </Stack>
    </Stack>
  );
}
